import React, { useEffect, useState, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { matchPaymentsAPI } from '@/lib/api';
import { openCheckout } from '@/lib/stripe';
import { FieldCollection, CollectorStripeCard } from '@/components/FieldCollection';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { CreditCard, Wallet, CheckCircle2, Clock, X, Users, Coins } from 'lucide-react';
import { getInitials, cn } from '@/lib/utils';

const METHOD_META = {
  online: { label: 'Online', Icon: CreditCard, color: 'text-sky-600 bg-sky-50 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900' },
  cash:   { label: 'Cash al campo', Icon: Wallet, color: 'text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900' },
  card:   { label: 'Carta in app', Icon: CreditCard, color: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900' },
  collector: { label: 'Raccoglie i soldi', Icon: Coins, color: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900' },
};

export const MatchPaymentsSection = ({ match, participants, currentUser }) => {
  const matchId = match?.match_id;
  const fee = match?.payment_amount;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Dialog "Aggiungi altri titolari"
  const [showPickDialog, setShowPickDialog] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [pickedIds, setPickedIds] = useState([]);

  const confirmedTitulars = useMemo(
    () => (participants || []).filter((p) => p.status === 'confirmed'),
    [participants],
  );

  const isMyselfConfirmed = useMemo(
    () => confirmedTitulars.some((p) => p.user_id === currentUser?.user_id),
    [confirmedTitulars, currentUser],
  );

  // Mappa user_id → participant info (hook qui sopra gli early-return per non violare le rules-of-hooks).
  const partByUid = useMemo(() => {
    const m = {};
    (participants || []).forEach((p) => { m[p.user_id] = p; });
    return m;
  }, [participants]);

  const fetchPayments = async () => {
    if (!matchId) return;
    try {
      const res = await matchPaymentsAPI.list(matchId);
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        // Visibility off: utente non titolare non admin → nascondi sezione
        setData({ hidden: true });
      } else {
        toast.error('Errore caricamento pagamenti');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  // In the app Stripe runs in the in-app browser: reload when the player closes it.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    const listener = Browser.addListener('browserFinished', () => fetchPayments());
    return () => { listener.then((l) => l.remove()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  if (loading) return null;                    // evita flash
  if (!data || data.hidden) return null;
  const collection = data.collection;
  // niente fee né raccolta → niente tracking (l'organizzatore vede solo "apri raccolta")
  const tracking = fee > 0 || !!collection;
  // Opened from the "collect the money" notification.
  const askedToCollect = new URLSearchParams(window.location.search).get('collega') === 'stripe';
  if (!tracking && !(data.is_admin && data.collections_enabled) && !askedToCollect) return null;

  const coverage = { ...(data.coverage || {}) };
  // Who collects doesn't pay: the server already skips their reminder and penalty.
  const collectorId = collection?.collector_type === 'user' ? collection.collector_id : null;
  if (collectorId && collectorId in coverage && !coverage[collectorId]) {
    coverage[collectorId] = { payment_id: null, payer_user_id: collectorId, method: 'collector', status: 'confirmed' };
  }
  const myCov = currentUser ? coverage[currentUser.user_id] : null;
  const isAdmin = !!data.is_admin;

  const openPick = (method) => {
    setSelectedMethod(method);
    setPickedIds([]);
    setShowPickDialog(true);
  };

  const payByCard = async (coversIds) => {
    setBusy(true);
    try {
      const res = await matchPaymentsAPI.checkout(matchId, {
        covers_user_ids: coversIds,
        origin: window.location.origin,
      });
      setShowPickDialog(false);
      await openCheckout(res.data.checkout_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Non riesco ad avviare il pagamento');
    } finally {
      setBusy(false);
    }
  };

  const submitPayment = async (method, coversIds) => {
    if (method === 'card') {
      payByCard(coversIds);
      return;
    }
    setBusy(true);
    try {
      await matchPaymentsAPI.create(matchId, { method, covers_user_ids: coversIds });
      toast.success(method === 'online' ? 'Pagamento online registrato' : 'Pagamento cash registrato');
      setShowPickDialog(false);
      fetchPayments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore registrazione pagamento');
    } finally {
      setBusy(false);
    }
  };

  const handleQuickPay = (method) => {
    submitPayment(method, [currentUser.user_id]);
  };

  const handlePayForOthers = () => {
    const covers = [currentUser.user_id, ...pickedIds];
    submitPayment(selectedMethod, covers);
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Annullare questo pagamento?')) return;
    setBusy(true);
    try {
      await matchPaymentsAPI.delete(matchId, paymentId);
      toast.success('Pagamento annullato');
      fetchPayments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setBusy(false); }
  };

  const handleConfirm = async (paymentId) => {
    setBusy(true);
    try {
      await matchPaymentsAPI.confirm(matchId, paymentId);
      toast.success('Pagamento confermato');
      fetchPayments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setBusy(false); }
  };

  const handleUnconfirm = async (paymentId) => {
    setBusy(true);
    try {
      await matchPaymentsAPI.unconfirm(matchId, paymentId);
      toast.success('Conferma rimossa');
      fetchPayments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setBusy(false); }
  };

  // Titolari non ancora coperti (per la select "pago anche per") — escluso me stesso
  const uncoveredOthers = confirmedTitulars
    .filter((p) => p.user_id !== currentUser?.user_id && !coverage[p.user_id]);

  const quota = collection
    ? collection.share + (collection.phase === 'late' ? collection.penalty.euro : 0)
    : Number(fee);

  const paidCount = Object.values(coverage).filter(Boolean).length;
  const totalCount = data.titular_count || confirmedTitulars.length;
  const progressPct = totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0;

  return (
    <>
      <Card data-testid="match-payments-section">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Coins className="h-5 w-5 text-emerald-600" />
            Tracciamento pagamenti
            {!collection && tracking && (
              <Badge variant="outline" className="ml-auto font-mono">
                €{Number(data.collected_total).toFixed(2)} / €{Number(data.expected_total).toFixed(2)}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {askedToCollect && <CollectorStripeCard matchId={matchId} />}
          <FieldCollection
            matchId={matchId}
            data={data}
            maxPlayers={match?.max_participants}
            currentUserId={currentUser?.user_id}
            myCov={myCov}
            isTitular={isMyselfConfirmed}
            onPayOthers={isMyselfConfirmed && uncoveredOthers.length > 0 ? () => openPick('card') : null}
            onChanged={fetchPayments}
          />

          {tracking && (<>
          {/* Progress bar */}
          <div>
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>{paidCount}/{totalCount} titolari hanno pagato</span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* Vista PLAYER: se sono titolare confermato */}
          {isMyselfConfirmed && (
            <div className="rounded-lg border bg-card p-3">
              {myCov ? (
                <MyPaymentBadge
                  cov={myCov}
                  payments={data.payments}
                  partByUid={partByUid}
                  currentUser={currentUser}
                  onDelete={handleDelete}
                  busy={busy}
                />
              ) : (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{collection ? 'Oppure paghi in contanti?' : 'Come paghi?'}</p>
                  <div className={`grid gap-2 ${collection ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    {/* With the in-app collection, "online" (paid on the organizer's link) would be a second card option. */}
                    {!collection && (
                    <Button
                      onClick={() => handleQuickPay('online')}
                      disabled={busy}
                      variant="outline"
                      className="h-auto py-3 flex-col gap-1 border-sky-200 hover:bg-sky-50 dark:hover:bg-sky-950/40"
                    >
                      <CreditCard className="h-5 w-5 text-sky-600" />
                      <span className="text-xs font-medium">Online</span>
                      <span className="text-[10px] text-muted-foreground">€{quota.toFixed(2)}</span>
                    </Button>
                    )}
                    <Button
                      onClick={() => handleQuickPay('cash')}
                      disabled={busy}
                      variant="outline"
                      className="h-auto py-3 flex-col gap-1 border-amber-200 hover:bg-amber-50 dark:hover:bg-amber-950/40"
                    >
                      <Wallet className="h-5 w-5 text-amber-600" />
                      <span className="text-xs font-medium">Cash al campo</span>
                      <span className="text-[10px] text-muted-foreground">€{quota.toFixed(2)}</span>
                    </Button>
                  </div>
                  {uncoveredOthers.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {!collection && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openPick('online')}
                          disabled={busy}
                          className="text-xs h-7"
                        >
                          <Users className="h-3.5 w-3.5 mr-1" /> Pago anche per altri (online)
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openPick('cash')}
                        disabled={busy}
                        className="text-xs h-7"
                      >
                        <Users className="h-3.5 w-3.5 mr-1" /> Pago anche per altri (cash)
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          </>)}

          {/* Vista ADMIN: tabella di tutti i pagamenti */}
          {isAdmin && tracking && (
            <AdminPaymentsTable
              payments={data.payments}
              coverage={coverage}
              titulars={confirmedTitulars}
              partByUid={partByUid}
              busy={busy}
              onDelete={handleDelete}
              onConfirm={handleConfirm}
              onUnconfirm={handleUnconfirm}
            />
          )}
        </CardContent>
      </Card>

      {/* Dialog "Pago anche per..." */}
      <Dialog open={showPickDialog} onOpenChange={setShowPickDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedMethod === 'cash'
                ? <Wallet className="h-5 w-5 text-amber-600" />
                : <CreditCard className={`h-5 w-5 ${selectedMethod === 'card' ? 'text-emerald-600' : 'text-sky-600'}`} />}
              Pago anche per…
            </DialogTitle>
            <DialogDescription>
              Seleziona i titolari per cui copri il pagamento ({METHOD_META[selectedMethod]?.label.toLowerCase()}).
              Quota: €{quota.toFixed(2)} a giocatore.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-64 border rounded-lg">
            <div className="p-2 space-y-1">
              {uncoveredOthers.length === 0 ? (
                <p className="text-sm text-muted-foreground p-3 text-center">
                  Tutti gli altri titolari hanno già pagato.
                </p>
              ) : uncoveredOthers.map((p) => {
                const checked = pickedIds.includes(p.user_id);
                return (
                  <label
                    key={p.user_id}
                    className={cn(
                      'flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors',
                      checked ? 'bg-primary/10' : 'hover:bg-muted',
                    )}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => {
                        setPickedIds((prev) =>
                          v ? [...prev, p.user_id] : prev.filter((x) => x !== p.user_id),
                        );
                      }}
                    />
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={p.user?.picture || p.picture} />
                      <AvatarFallback>{getInitials(p.user?.name || p.name)}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{p.user?.name || p.name}</span>
                  </label>
                );
              })}
            </div>
          </ScrollArea>

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Totale: <strong>€{(quota * (1 + pickedIds.length)).toFixed(2)}</strong>
            </span>
            <span className="text-xs text-muted-foreground">
              ({1 + pickedIds.length} {pickedIds.length === 0 ? 'giocatore' : 'giocatori'})
            </span>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPickDialog(false)}>Annulla</Button>
            <Button onClick={handlePayForOthers} disabled={busy}>
              {selectedMethod === 'card' ? 'Paga con carta' : 'Conferma pagamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const MyPaymentBadge = ({ cov, payments, partByUid, currentUser, onDelete, busy }) => {
  const meta = METHOD_META[cov.method];
  const fullPayment = payments.find((p) => p.payment_id === cov.payment_id);
  const isPayer = cov.payer_user_id === currentUser?.user_id;
  const payerName = isPayer
    ? 'te'
    : (partByUid[cov.payer_user_id]?.user?.name || partByUid[cov.payer_user_id]?.name || 'un compagno');
  const canDelete = isPayer && cov.status === 'pending';

  return (
    <div className="flex items-center gap-3">
      <div className={cn(
        'h-10 w-10 rounded-lg flex items-center justify-center shrink-0 border',
        meta.color,
      )}>
        <meta.Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">
            {cov.method === 'collector' ? 'Raccogli tu: non paghi la quota' : cov.status === 'held' ? 'Quota bloccata' : isPayer ? 'Hai pagato' : `Pagato da ${payerName}`}
          </span>
          <Badge variant="outline" className={cn('text-xs', meta.color)}>{meta.label}</Badge>
          {cov.status === 'confirmed' ? (
            <Badge className="bg-emerald-600 hover:bg-emerald-600 text-xs">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Confermato
            </Badge>
          ) : cov.status === 'held' ? (
            <Badge variant="outline" className="text-xs">
              <Clock className="h-3 w-3 mr-1" /> Bloccata sulla carta
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs">
              <Clock className="h-3 w-3 mr-1" /> In attesa
            </Badge>
          )}
        </div>
        {fullPayment && fullPayment.covers_user_ids.length > 1 && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Copre {fullPayment.covers_user_ids.length} giocatori · €{Number(fullPayment.amount).toFixed(2)}
          </p>
        )}
      </div>
      {canDelete && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={() => onDelete(cov.payment_id)}
          disabled={busy}
          title="Annulla pagamento"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

const AdminPaymentsTable = ({
  payments, coverage, titulars, partByUid, busy, onDelete, onConfirm, onUnconfirm,
}) => {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        Stato per titolare
      </p>
      <div className="border rounded-lg divide-y">
        {titulars.map((t) => {
          const cov = coverage[t.user_id];
          const payment = cov ? payments.find((p) => p.payment_id === cov.payment_id) : null;
          const meta = cov ? METHOD_META[cov.method] : null;
          const payer = payment ? (partByUid[payment.payer_user_id]?.user || partByUid[payment.payer_user_id]) : null;
          const isProxy = cov && cov.payer_user_id !== t.user_id;
          const manual = cov && (cov.method === 'online' || cov.method === 'cash');  // card and collector aren't edited by hand
          return (
            <div
              key={t.user_id}
              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 py-2.5"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={t.user?.picture || t.picture} />
                  <AvatarFallback>{getInitials(t.user?.name || t.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{t.user?.name || t.name}</p>
                  {isProxy && payer && (
                    <p className="text-[11px] text-muted-foreground truncate">
                      pagato da {payer.name || payer.user_id}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap justify-end shrink-0 pl-11 sm:pl-0">
                {cov ? (
                  <>
                    <Badge variant="outline" className={cn('text-xs whitespace-nowrap', meta.color)}>
                      <meta.Icon className="h-3 w-3 mr-1" /> {meta.label}
                    </Badge>
                    {cov.status === 'confirmed' ? (
                      <Badge className="bg-emerald-600 hover:bg-emerald-600 text-xs whitespace-nowrap">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> OK
                      </Badge>
                    ) : cov.method === 'card' ? (
                      <Badge variant="outline" className="text-xs whitespace-nowrap">
                        <Clock className="h-3 w-3 mr-1" /> Bloccata
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs whitespace-nowrap"
                        onClick={() => onConfirm(cov.payment_id)}
                        disabled={busy}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Conferma
                      </Button>
                    )}
                    {cov.status === 'confirmed' && manual && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-muted-foreground hover:text-amber-600"
                        onClick={() => onUnconfirm(cov.payment_id)}
                        disabled={busy}
                        title="Rimuovi conferma"
                      >
                        <Clock className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {manual && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => onDelete(cov.payment_id)}
                        disabled={busy}
                        title="Elimina pagamento"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </>
                ) : (
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    Da pagare
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MatchPaymentsSection;
